import { MAUL } from '@/content/weapons';
import { Arena } from '@/game/arena';
import { View } from '@/render/view';
import { createImpactAudio } from '@/audio/impact';
import type { PointerState } from '@/sim/hero';

const canvas = document.getElementById('stage') as HTMLCanvasElement;
const hint = document.getElementById('hint') as HTMLElement;

const HALF_W = 22;
const HALF_H = 14;

const view = new View(canvas);
const arena = new Arena(MAUL, HALF_W, HALF_H);

const ANCHORS: readonly [number, number][] = [
  [-13, -8],
  [13, -8],
  [-13, 8],
  [13, 8]
];
for (const [ax, ay] of ANCHORS) arena.anchor(ax, ay);

for (let i = 0; i < 8; i += 1) {
  const a = (i / 8) * Math.PI * 2 + 0.4;
  arena.spawn(Math.cos(a) * 9, Math.sin(a) * 6);
}

view.syncWalls(arena.world);
view.setBounds(HALF_W, HALF_H);

const audio = createImpactAudio();
const title = document.getElementById('title') as HTMLElement;
const goal = document.getElementById('goal') as HTMLElement;
let started = false;

function begin(): void {
  if (started) return;
  started = true;
  void audio.resume();
  title.classList.add('is-gone');
  hint.textContent = 'Swing hard. Break the anchors.';
  hint.style.opacity = '1';
  window.setTimeout(() => {
    hint.style.opacity = '0';
  }, 4200);
}
title.addEventListener('pointerdown', begin);
window.addEventListener('keydown', begin);

const pointer: PointerState = { x: 0, y: 0, planted: false, active: false };
let pointerPx = { x: 0, y: 0 };
let touched = false;

function toWorld(cx: number, cy: number): void {
  const r = canvas.getBoundingClientRect();
  const [wx, wy] = view.screenToWorld(cx - r.left, cy - r.top, r.width, r.height);
  pointer.x = wx;
  pointer.y = wy;
}

function markTouched(): void {
  if (touched) return;
  touched = true;
}

canvas.addEventListener('pointermove', (e) => {
  pointerPx = { x: e.clientX, y: e.clientY };
  pointer.active = true;
  markTouched();
});
canvas.addEventListener('pointerdown', (e) => {
  pointerPx = { x: e.clientX, y: e.clientY };
  pointer.active = true;
  pointer.planted = true;
  markTouched();
  canvas.setPointerCapture(e.pointerId);
});
canvas.addEventListener('pointerup', () => {
  pointer.planted = false;
});
canvas.addEventListener('pointercancel', () => {
  pointer.planted = false;
});

function resize(): void {
  const w = window.innerWidth;
  const h = window.innerHeight;
  canvas.width = Math.floor(w * Math.min(devicePixelRatio, 2));
  canvas.height = Math.floor(h * Math.min(devicePixelRatio, 2));
  view.resize(w, h, devicePixelRatio);
}
window.addEventListener('resize', resize);
resize();

let last = performance.now();

function frame(now: number): void {
  requestAnimationFrame(frame);
  const elapsed = Math.min(0.25, (now - last) / 1000);
  last = now;

  toWorld(pointerPx.x, pointerPx.y);
  arena.frameUpdate(started ? elapsed : 0, pointer);

  for (let i = 0; i < arena.impacts.length; i += 1) {
    const hit = arena.impacts[i];
    view.burst(hit.x, hit.y, Math.min(1, hit.speed / 20), hit.killed);
    audio.play({
      speed: hit.speed,
      reducedMass: hit.mass,
      material: hit.material,
      grazing: hit.grazing,
      emitter: hit.emitter,
      pan: Math.max(-1, Math.min(1, hit.x / HALF_W))
    });
  }

  const head = arena.world.bodies[arena.hero.head];
  const speed = Math.hypot(head.vx, head.vy);
  view.mark(head.x, head.y, Math.min(1, speed / 22), Math.atan2(head.vy, head.vx));

  if (started) {
    const left = arena.totalAnchors() - arena.broken;
    goal.textContent = arena.open
      ? 'The way is open'
      : `${left} anchor${left === 1 ? '' : 's'} standing`;
  }

  view.sync(arena.world, arena.hero.head, arena.chain());
  const focus = arena.heroPosition();
  view.render(
    focus.x + arena.response.offsetX(),
    focus.y + arena.response.offsetY(),
    arena.response.roll(),
    elapsed
  );
}

requestAnimationFrame(frame);
